import { RiCheckFill, RiCloseFill } from "react-icons/ri";
import { makeStyles } from "@material-ui/core";
import { createFormikValidation, formatBRL, isset } from "../../../utils/Utils";

export const indexPage = () => {
    return "/financeiro";
};

export const getInitialValues = (data) => {
    return {
        plan: data?.planCode ?? data?.customPlan?.id ?? "",
        card_number: "",
        card_due_date: null,
        card_cvv: "",
        card_name: "",
        card_cpf_cnpj: "",
        postalcode: data?.postalcode ?? "",
        address: data?.address ?? "",
        number: data?.number ?? "",
        neighborhood: data?.neighborhood ?? "",
        city: data?.city ?? "",
        uf: data?.uf ?? "",
    };
};

export const validateForm = (values, userData) => {
    const map = new Map();

    if ([values.card_cvv, values.card_due_date, values.card_name, values.card_cpf_cnpj, values.card_number].some((item) => Boolean(item))) {
        map.set("card_number", "Número do cartão");
        map.set("card_due_date", "Data de vencimento");
        map.set("card_cvv", "CVV");
        map.set("card_name", "Nome do titular");
        map.set("card_cpf_cnpj", "CPF/CNPJ do titular");
    }

    if ([values.postalcode, values.address, values.number, values.neighborhood].some((item) => Boolean(item)) || values.city !== userData?.city || values.uf !== userData?.uf) {
        map.set("postalcode", "CEP");
        map.set("address", "Logradouro");
        map.set("number", "Número");
        map.set("neighborhood", "Bairro");
        map.set("city", "Cidade");
        map.set("uf", "Estado");
    }

    map.set("plan", "Selecione um plano");

    const errors = createFormikValidation(map, values);

    return errors;
};

export const useStyles = makeStyles({
    linearProgressRoot: {
        height: 8,
        borderRadius: 4,
        backgroundColor: "#1F2A37",
    },
    linearProgressBar: {
        backgroundColor: "#9D66FF",
    },
});

export const getCycleLabel = (cycle) => {
    switch (cycle) {
        case "WEEKLY":
            return "semana";
        case "MONTHLY":
            return "mês";
        case "YEARLY":
            return "ano";
        default:
            return "";
    }
};

const cycleRecurrenceLabels = {
    WEEKLY: { singular: "semana", plural: "semanas" },
    MONTHLY: { singular: "mês", plural: "meses" },
    YEARLY: { singular: "ano", plural: "anos" },
};

export const getCouponRemainingLabel = (cycle, remaining) => {
    const label = cycleRecurrenceLabels[cycle] ?? { singular: "ciclo", plural: "ciclos" };
    if (remaining === 0) return `Último ${label.singular} com desconto`;
    if (remaining === 1) return `1 ${label.singular} restante com desconto`;
    return `${remaining} ${label.plural} restantes com desconto`;
};

export const getCouponRecurrenceLabel = (cycle, count) => {
    const label = cycleRecurrenceLabels[cycle] ?? { singular: "ciclo", plural: "ciclos" };
    if (count === 1) return `Desconto válido por 1 ${label.singular}`;
    return `Desconto válido por ${count} ${label.plural}`;
};

export const PlanPriceLabel = ({ price, cycle, originalPrice, size = "large" }) => (
    <span className={`${size === "large" ? "tt-32" : "tt-24"} medium color-white`} style={{ overflow: "overlay" }}>
        {originalPrice !== 0 ? (
            <>
                {originalPrice != null && originalPrice !== price && (
                    <span className={`${size === "large" ? "tt-20" : "tt-16"} regular color-dark-6`} style={{ textDecoration: "line-through", textDecorationThickness: 1, marginRight: 8 }}>
                        {formatBRL(originalPrice)}
                    </span>
                )}
                {price !== 0 ? (
                    <>
                        {formatBRL(price ?? 0)}
                        <span className={`${size === "large" ? "tt-24" : "tt-20"} regular color-dark-6`}>/{getCycleLabel(cycle)}</span>
                    </>
                ) : (
                    "Gratuito"
                )}
            </>
        ) : (
            "Gratuito"
        )}
    </span>
);

export const PlanCard = ({ plan, funcionalidades, coupon, isMobileScreen }) => {
    const renderIcon = (bool) => (bool ? <RiCheckFill size={16} color="#13C296" /> : <RiCloseFill size={16} color="#f23030" />);

    return (
        <div className="d-flex flex-column gap-24">
            <div className="d-flex flex-column gap-8" style={{ borderBottom: "1px solid #374151", paddingBottom: 24 }}>
                <span className="tt-20 medium color-white">{plan.name}</span>

                <PlanPriceLabel price={plan.price} cycle={plan.cycle} originalPrice={plan.originalPrice} size={isMobileScreen ? "small" : "large"} />

                {coupon?.cpd_limit_recurrence && (
                    <span className="tt-14" style={{ color: "#13C296" }}>
                        {getCouponRecurrenceLabel(plan.cycle, coupon.cpd_limit_recurrence)}
                    </span>
                )}
            </div>

            <div className="d-flex flex-column gap-4">
                {(!isset(funcionalidades) || funcionalidades.Agentes) && (
                    <>
                        <div className="d-flex gap-8 align-items-center">
                            {renderIcon(plan.benefits?.agent?.msgs != 0)}
                            <span className="tt-16 color-dark-7">{plan.benefits?.agent?.msgs == -1 ? "Mensagens ilimitadas para agentes" : !plan.benefits?.agent?.msgs ? "Mensagens para agentes" : `${plan.benefits?.agent?.msgs} mensage${plan.benefits?.agent?.msgs > 1 ? "ns" : "m"} para agentes`}</span>
                        </div>

                        <div className="d-flex gap-8 align-items-center">
                            {renderIcon(plan.benefits?.agent?.trainings != 0)}
                            <span className="tt-16 color-dark-7">
                                {plan.benefits?.agent?.trainings == -1 ? "Treinamentos ilimitados de agentes" : !plan.benefits?.agent?.trainings ? "Treinamento de agentes" : `${plan.benefits?.agent?.trainings} treinamento${plan.benefits?.agent?.trainings > 1 ? "s" : ""} de agentes`}
                            </span>
                        </div>
                    </>
                )}

                {(!isset(funcionalidades) || funcionalidades.GeradorImagem) && (
                    <div className="d-flex gap-8 align-items-center">
                        {renderIcon(plan.benefits?.images?.count != 0)}
                        <span className="tt-16 color-dark-7">
                            {plan.benefits?.images?.count == -1 ? "Imagens geradas ilimitadas" : !plan.benefits?.images?.count ? "Gerar imagens" : `${plan.benefits?.images?.count} image${plan.benefits?.images?.count > 1 ? "ns" : "m"} gerada${plan.benefits?.images?.count > 1 ? "s" : ""}`}
                        </span>
                    </div>
                )}

                {(!isset(funcionalidades) || funcionalidades.Experts) && (
                    <div className="d-flex gap-8 align-items-center">
                        {renderIcon(plan.benefits?.expertChat?.msgs != 0)}
                        <span className="tt-16 color-dark-7">{plan.benefits?.expertChat?.msgs == -1 ? "Mensagens ilimitadas para experts" : !plan.benefits?.expertChat?.msgs ? "Mensagens para experts" : `${plan.benefits?.expertChat?.msgs} mensage${plan.benefits?.expertChat?.msgs > 1 ? "ns" : "m"} para experts`}</span>
                    </div>
                )}

                <div className="d-flex gap-8 align-items-center">
                    {renderIcon(plan.benefits?.users?.count != 0)}
                    <span className="tt-16 color-dark-7">{plan.benefits?.users?.count == -1 ? "Usuários ativos ilimitados" : !plan.benefits?.users?.count ? "Novos usuários" : `${plan.benefits?.users?.count} usuário${plan.benefits?.users?.count > 1 ? "s" : ""} ativo${plan.benefits?.users?.count > 1 ? "s" : ""}`}</span>
                </div>
            </div>
        </div>
    );
};
